import { ReactElement, FC, ReactNode, useEffect, MouseEventHandler, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getCurrentRouteAndParams } from '../../App';
import { isDoctor } from '../../pages/utils';
import {
    GO_TO_APPOINTMENTS,
    GO_TO_PAYMENTS_DASHBOARD,
    GO_TO_ANALYTICS_DASHBOARD,
    GO_TO_HE_DASHBOARD,
    GO_TO_ALL_PATIENTS,
    GO_TO_ABHA_DASHBOARD,
    IPD_LIST,
} from '../../routes/paths';
import { showDialog } from '../../store/actions/prescriptionPad';
import {
    MISC_KEYS,
    getDoc,
    getItemFromMisc,
    getSelectedClinicId,
    getSingleDoctor,
    isSingleDoctorAccess,
} from '../../store/idbHandler';
import { GlobalState } from '../../store/reducers';
import {
    getActiveOrderUpdatesCount,
    getClaimableAmount,
    getGenieSetting,
    getMonthlyAbhaLoss,
} from '../../store/selectors/session';
import { track } from '../../tracking';
import { AntTooltip } from './AntTooltip';
import { dispatch as ekaEventDispath } from '../../bridge/events/dispatch';
import { OPEN_TRIAL_PERSUASION_POPUP } from '../../bridge/events/types';
import { isDoctorInternal, isDoctorNOSA } from '../../store/utils';
import { ABHAIcon, EkaPlusIcon, EkaProIcon, ipdHospitalIcon } from './Icons';
import { Popover } from 'antd';
import { GenieSettings, SREV2 } from '../../api/pushGenie';
import ActiveOrderUpdates from './ActiveOrderUpdates';
import { Order } from '../../api/fetchAllHEOrder';
import { AnimatePresence } from 'framer-motion';
import { BookLabTestsDialog } from '../prescriptionPad/rightPanel/finishScreen/BookLabTests';
import { OrderMedicineDialog } from '../prescriptionPad/rightPanel/finishScreen/OrderMedicine';
import { ACTIVE_TAB } from '../../pages/HEDashboard';
import { getMyDbId } from '../../api/mactiveid';
import { HUB } from '../../api/constants';
import { getMActiveId } from '../../store/localstorage';
import { Ipd } from '../../api/fetchMyGenie';
import { getAclViewClassName } from '../../restrictions/utils';

export enum TOOL_PLANS {
    PLUS = 'plus',
    PRO = 'pro',
}

export const TrialChip: React.FC<{
    className?: { plus?: string; pro?: string; trail?: string };
}> = (props) => {
    const doc = getSingleDoctor();
    if (!doc) {
        return null;
    }

    const isNosa = isDoctorNOSA(doc);
    const isInternal = isDoctorInternal(doc);
    if (isNosa || isInternal) return null;

    const saas = doc?.profile.professional.saas;
    const { status } = doc?.profile.professional.saas_detail || {};
    const isTrial = !status;

    const handleTrialClick: MouseEventHandler<HTMLDivElement> = (e) => {
        e.stopPropagation();
        ekaEventDispath({
            type: OPEN_TRIAL_PERSUASION_POPUP,
        });
    };

    if (isTrial) {
        return null;
        // <span className="cursor-pointer" onClick={handleTrialClick}>
        //     <TrialIcon className={props.className?.trail} />
        // </span>
    }

    if (saas && !isTrial) {
        return (
            <span>
                {saas === 'APT' ? (
                    <EkaPlusIcon className={props.className?.plus} />
                ) : (
                    <EkaProIcon className={props.className?.pro} />
                )}
            </span>
        );
    }

    return null;
};

export const NAV_BAR_HOME = 'NAV_BAR_HOME';
export const NAV_BAR_CALENDAR = 'NAV_BAR_CALENDAR';
export const NAV_BAR_SYNC = 'NAV_BAR_SYNC';
export const NAV_BAR_PROFILE = 'NAV_BAR_PROFILE';
export const NAV_BAR_SETTINGS = 'NAV_BAR_SETTINGS';
export const NAV_BAR_SUBSCRIBE = 'NAV_BAR_SUBSCRIBE';
export const NAV_BAR_BOOK_TRAINING = 'NAV_BAR_BOOK_TRAINING';
export const NAV_BAR_MY_RX_TEMPLATE = 'NAV_BAR_MY_RX_TEMPLATE';
export const NAV_BAR_MY_GENIE = 'NAV_BAR_MY_GENIE';
export const NAV_BAR_PATIENT_REFERRAL = 'NAV_BAR_PATIENT_REFERRAL';
export const NAV_BAR_PAYMENTS = 'NAV_BAR_PAYMENTS';
export const NAV_BAR_HE_DASHBOARD = 'NAV_BAR_HE_DASHBOARD';
export const NAV_BAR_ANALYTICS = 'NAV_BAR_ANALYTICS';
export const NAV_BAR_ABHA_DASHBOARD = 'NAV_BAR_ABHA_DASHBOARD';
export const NAV_BAR_GMB = 'NAV_BAR_GMB';
export const NAV_BAR_PATIENT = 'NAV_BAR_PATIENT';
export const NAV_BAR_NEW_PATIENT = 'NAV_BAR_NEW_PATIENT';
export const NAV_BAR_PATIENT_DETAILS = 'NAV_BAR_PATIENT_DETAILS';
export const NAV_BAR_KHATA = 'NAV_BAR_KHATA';
export const NAV_BAR_ADD_EKA_CREDITS = 'NAV_BAR_ADD_EKA_CREDITS';
export const NAV_BAR_IPD_LIST = 'NAV_BAR_IPD_LIST';

export type NAVBAR_NAMES =
    | typeof NAV_BAR_HOME
    | typeof NAV_BAR_CALENDAR
    | typeof NAV_BAR_SYNC
    | typeof NAV_BAR_PROFILE
    | typeof NAV_BAR_PAYMENTS
    | typeof NAV_BAR_HE_DASHBOARD
    | typeof NAV_BAR_ANALYTICS
    | typeof NAV_BAR_ABHA_DASHBOARD
    | typeof NAV_BAR_GMB
    | typeof NAV_BAR_PATIENT
    | typeof NAV_BAR_SETTINGS
    | typeof NAV_BAR_BOOK_TRAINING
    | typeof NAV_BAR_MY_RX_TEMPLATE
    | typeof NAV_BAR_MY_GENIE
    | typeof NAV_BAR_SUBSCRIBE
    | typeof NAV_BAR_NEW_PATIENT
    | typeof NAV_BAR_PATIENT_DETAILS
    | typeof NAV_BAR_KHATA
    | typeof NAV_BAR_ADD_EKA_CREDITS
    | typeof NAV_BAR_PATIENT_REFERRAL
    | typeof NAV_BAR_IPD_LIST;

type NavItem = {
    id?: string;
    showNewLabel?: boolean;
    className?: string;
    label: string | JSX.Element;
    onClick?: () => void;
    active?: boolean;
    icon: ReactNode;
    showTooltip?: boolean;
    showLabel?: boolean;
    classNameInner?: string;
};

export const NavItem: FC<NavItem> = ({
    onClick,
    active,
    icon,
    label,
    showNewLabel,
    id,
    className,
    classNameInner,
    showLabel = true,
    showTooltip = true,
}) => {
    const isHealthWeekDoctorFlag = useSelector((state: GlobalState) =>
        getGenieSetting(state, 'isHealthWeekDoctor'),
    );
    const isHealthWeekDoctor = isHealthWeekDoctorFlag && isDoctor();
    return (
        <div
            id={id}
            onClick={onClick}
            className={`navitem_new ${active ? 'active' : ''} ${
                className ? className : ''
            } hover:bg-darwin-accent-bright-blue-100 hover:rounded-8`}
            style={
                isHealthWeekDoctor
                    ? { backgroundColor: 'transparent', borderLeftWidth: 0 }
                    : { borderLeftWidth: 0 }
            }
        >
            <div
                className={`flex flex-col items-center justify-center navitem-box ${
                    classNameInner ? classNameInner : ''
                }`}
            >
                {showTooltip ? (
                    <AntTooltip placement="right" content={label}>
                        {icon}
                    </AntTooltip>
                ) : (
                    icon
                )}
                {label && showLabel && (
                    <p
                        className={`text-10 ${
                            active
                                ? 'text-darwin-accent-bright-blue-800'
                                : 'text-darwin-neutral_new-800'
                        } text-center max-w-64 font-700`}
                    >
                        {label}
                    </p>
                )}
            </div>
            {showNewLabel && (
                <div className="mt-5 mx-auto w-50 py-1 text-center body-3 bg-darwin-accent-medical-history-orange-800 rounded-4 text-white">
                    New
                </div>
            )}
        </div>
    );
};

let isFrilScriptLoaded = false;
// We need to keep a reference to the widget instance so we can cleanup
// when the component unmounts
let widget: FrillWidget;

// Define our config. You can get the key from the Widget code snippet
const config: FrillConfig = {
    key: '542d14e0-5ff2-41d8-bbb3-a29705de2fc3', // <-- Add Widget key here
    callbacks: {
        // This will be called when the widget is loaded
        onReady: (frillWidget) => {
            const doc = getDoc();
            widget = frillWidget;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            widget.identify({
                email: `${doc?._id}@eka.do`,
                name: [doc?.profile.personal.name.f, doc?.profile.personal.name.l]
                    .filter(Boolean)
                    .join(' '),
            });
        },
    },
};

export default (): ReactElement => {
    const [isABHA, setIsABHA] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const activeOrderUpdatesCount = useSelector(getActiveOrderUpdatesCount);
    const [currentOrder, setCurrentOrder] = useState<Order>();
    const claimableAmount = useSelector(getClaimableAmount);
    const clinic_id = getSelectedClinicId();
    const currClinicClaimableAmount = claimableAmount?.find((val) => val.clinic_id === clinic_id);

    const [isAbhaPopoverVisible, setIsAbhaPopoverVisible] = useState(
        !!(currClinicClaimableAmount?.amount && !currClinicClaimableAmount?.closed),
    );
    const [isIpdEnabled, setIsIpdEnabled] = useState(false);

    useEffect(() => {
        const myId = getMActiveId();
        if (!myId) {
            return;
        }
        getItemFromMisc<Ipd>(myId, MISC_KEYS.ipd)
            .then((i) => {
                setIsIpdEnabled(i?.enabled);
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        if (isFrilScriptLoaded) {
            return;
        }
        const element = document.querySelector('body');
        const script = document.createElement('script');
        script.src = 'https://widget.frill.co/v2/widget.js';
        script.onload = () => {
            isFrilScriptLoaded = true;
            loadFrill();
        };
        element?.appendChild(script);
    }, []);

    const loadFrill = () => {
        try {
            // Let's check if the Frill script has already loaded
            if ('Frill' in window) {
                widget?.destroy();
                window.Frill_Config = window.Frill_Config.filter((c) => c !== config);
                // If the Frill api is already available we can create the widget now
                widget = window.Frill.widget({ ...config });
            } else {
                // If the Frill api hasn't been loaded, we need to add our config to the list.
                // When the api loads, it will create all widgets in the Frill_Config list and dispatch the
                // config.callbacks.onReady event for each
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.Frill_Config = window.Frill_Config || [];
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.Frill_Config.push(config);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchABHADetails = async () => {
        const settings = await getItemFromMisc<GenieSettings>(getMyDbId() || '', 'settings');
        setIsABHA(settings?.abdm ? true : false);
    };

    useEffect(() => {
        loadFrill();
        fetchABHADetails();
    }, []);

    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const isHealthWeekDoctorFlag = useSelector((state: GlobalState) =>
        getGenieSetting(state, 'isHealthWeekDoctor'),
    );
    const currMonthLoss = useSelector(getMonthlyAbhaLoss);
    const isHealthWeekDoctor = isHealthWeekDoctorFlag && isDoctor();

    const onHome = (): void => {
        history.push(GO_TO_APPOINTMENTS(getSelectedClinicId() || ''));
        track(NAV_BAR_HOME);
    };

    const onPatient = (): void => {
        history.push(GO_TO_ALL_PATIENTS(getSelectedClinicId() || ''));
        track(NAV_BAR_PATIENT);
    };

    const onPaymentsDashboard = (): void => {
        history.push(GO_TO_PAYMENTS_DASHBOARD('all-receipts', getSelectedClinicId() || ''));
        track(NAV_BAR_PAYMENTS);
    };

    const onHEDashboard = (): void => {
        history.push(GO_TO_HE_DASHBOARD('not-ordered'));
        track(NAV_BAR_HE_DASHBOARD);
    };

    const onAnalytics = (): void => {
        track(NAV_BAR_ANALYTICS);

        history.push(GO_TO_ANALYTICS_DASHBOARD());
    };

    const onABHADashboard = (): void => {
        history.push(GO_TO_ABHA_DASHBOARD);
        track(NAV_BAR_ABHA_DASHBOARD);
    };

    const showHealthWeek = () => {
        dispatch(showDialog('health-week'));
    };

    const viewOrder = (order?: Order): void => {
        setCurrentOrder(order);
        setIsVisible(false);
        if (order) track('ACTIVE_ORDER_POPUP_VIEW_ORDER_CLICK', { type: order.type });
    };

    const onIpdList = (): void => {
        track(NAV_BAR_IPD_LIST);

        history.push(IPD_LIST);
    };

    const currentRoute = getCurrentRouteAndParams(location.pathname, location.search);
    const doc = getSingleDoctor();

    const pxId = currentOrder?.pxid || '';
    const patientId = currentOrder?.patient_id || '';
    const patientName = currentOrder?.patient_name || '';
    const phoneNumber = currentOrder?.customer_details?.mobile || '';
    const gender = currentOrder?.customer_details?.gender?.[0]?.toUpperCase() || '';
    const age = currentOrder?.customer_details?.age || 0;
    const orderId = currentOrder?.order_id;

    return (
        <>
            <div
                className={`overflow-auto hide-scrollbar grid w-64 h-full grid-flow-row ${
                    !isHealthWeekDoctor ? 'bg-white' : ''
                } border-r justify-items-center border-neutral-8 pb-16 z-0`}
                style={
                    isHealthWeekDoctor
                        ? { background: 'linear-gradient(180deg, #BFD6B8 0%, #FFF 47.83%)' }
                        : undefined
                }
            >
                <div className="flex flex-col items-start self-start justify-start w-full">
                    <div
                        onClick={onHome}
                        className={`w-full flex flex-col gap-4 items-center pt-24 pb-12 z-1 sticky top-0 ${
                            isHealthWeekDoctor ? '' : 'bg-white'
                        }`}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width="32"
                            height="32"
                            className="m-auto cursor-pointer fill-darwin-accent-bright-blue-800"
                        >
                            <path d="M22,7a4.34,4.34,0,0,1-1.26,2.81L13.3,17.27a5.46,5.46,0,0,1-.64.53,4.33,4.33,0,0,1-6.72-4h0A1.45,1.45,0,1,1,8.36,15,2,2,0,0,0,11,16.1h0l.19-.08a2.59,2.59,0,0,0,.5-.37L15.33,12,11.67,8.29a2,2,0,0,0-2.83,0,1.91,1.91,0,0,0-.53.91,1.45,1.45,0,0,1-1.75,2.32,1.51,1.51,0,0,1-.57-1A4.32,4.32,0,0,1,13.3,6.69L17,10.33,19.1,8.22a2,2,0,0,0,.07-2.78,9.7,9.7,0,1,0-.67,13.7h0c.23-.21.45-.43.66-.65a2,2,0,0,0-.07-2.78l-1.55-1.55,1.63-1.63,1.55,1.55a4.31,4.31,0,0,1,.15,6,12,12,0,1,1-.8-17c.27.26.54.53.8.81A4.27,4.27,0,0,1,22,7Z" />
                        </svg>
                        <TrialChip />
                    </div>

                    {isHealthWeekDoctor && (
                        <p
                            className="text-10 uppercase w-48 italic m-auto rounded-4 text-white font-700 text-center py-2 leading-4 mb-4 cursor-pointer"
                            style={{
                                background:
                                    'linear-gradient(98deg, #19A66A -12.79%, #0F6440 124.67%)',
                            }}
                            onClick={showHealthWeek}
                        >
                            Health week
                        </p>
                    )}
                    <NavItem
                        onClick={onHome}
                        className="fill-darwin-neutral_new-800"
                        label="Queue"
                        active={
                            !currentRoute ||
                            !currentRoute.navBarName ||
                            currentRoute.navBarName === NAV_BAR_HOME
                        }
                        icon={
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                className="fill-darwin-neutral_new-800"
                            >
                                <path d="M20.9688 12C20.9688 12.5625 20.5 13 19.9688 13H18.9688L19 18C19 18.0938 19 18.1875 19 18.25V18.75C19 19.4688 18.4375 20 17.75 20H17.25C17.1875 20 17.1562 20 17.125 20C17.0938 20 17.0312 20 17 20H16H15.25C14.5312 20 14 19.4688 14 18.75V18V16C14 15.4688 13.5312 15 13 15H11C10.4375 15 10 15.4688 10 16V18V18.75C10 19.4688 9.4375 20 8.75 20H8H7C6.9375 20 6.90625 20 6.84375 20C6.8125 20 6.78125 20 6.75 20H6.25C5.53125 20 5 19.4688 5 18.75V15.25C5 15.25 5 15.2188 5 15.1875V13H4C3.4375 13 3 12.5625 3 12C3 11.7188 3.09375 11.4688 3.3125 11.25L11.3125 4.25C11.5312 4.03125 11.7812 4 12 4C12.2188 4 12.4688 4.0625 12.6562 4.21875L20.625 11.25C20.875 11.4688 21 11.7188 20.9688 12Z" />
                            </svg>
                        }
                    />
                    <NavItem
                        onClick={onPatient}
                        className={`fill-darwin-neutral_new-800 ${getAclViewClassName(
                            SREV2.ALL_PT,
                        )}`}
                        label="Patients"
                        active={
                            currentRoute &&
                            (currentRoute.navBarName === NAV_BAR_PATIENT ||
                                currentRoute.navBarName === NAV_BAR_NEW_PATIENT ||
                                currentRoute.navBarName === NAV_BAR_PATIENT_DETAILS)
                                ? true
                                : false
                        }
                        icon={
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                className="fill-darwin-neutral_new-800"
                            >
                                <rect width="24" height="24" rx="8" fill="none" />
                                <path d="M4.5 6V12.5H7.25C7.65625 12.5 8 12.8438 8 13.25C8 13.6875 7.65625 14 7.25 14H4.5V15.5H7.25C7.65625 15.5 8 15.8438 8 16.25C8 16.6875 7.65625 17 7.25 17H4.5V18C4.5 18.2812 4.71875 18.5 5 18.5H11C11 18.6562 11 18.7812 11 18.9062C11 19.3125 11.0938 19.6875 11.2812 20C11.1875 20 11.0938 20 11 20H5C3.875 20 3 19.125 3 18V6C3 4.90625 3.875 4 5 4H11C12.0938 4 13 4.90625 13 6V15.5C12.3438 15.8438 11.8438 16.375 11.5 17V6C11.5 5.75 11.25 5.5 11 5.5H5C4.71875 5.5 4.5 5.75 4.5 6ZM7.25 7.5C7.25 7.25 7.46875 7 7.75 7H8.25C8.5 7 8.75 7.25 8.75 7.5V8.25H9.5C9.75 8.25 10 8.5 10 8.75V9.25C10 9.53125 9.75 9.75 9.5 9.75H8.75V10.5C8.75 10.7812 8.5 11 8.25 11H7.75C7.46875 11 7.25 10.7812 7.25 10.5V9.75H6.5C6.21875 9.75 6 9.53125 6 9.25V8.75C6 8.5 6.21875 8.25 6.5 8.25H7.25V7.5ZM19 12.5C19 13.9062 17.875 15 16.5 15C15.0938 15 14 13.9062 14 12.5C14 11.125 15.0938 10 16.5 10C17.875 10 19 11.125 19 12.5ZM12 18.9375C12 17.3125 13.2812 16 14.9062 16H18.0625C19.6875 16 21 17.3125 21 18.9375C21 19.5312 20.5 20 19.9062 20H13.0625C12.4688 20 12 19.5312 12 18.9375Z" />
                            </svg>
                        }
                    />
                    {isSingleDoctorAccess() && (
                        <Popover
                            content={<ActiveOrderUpdates viewOrder={viewOrder} />}
                            title={null}
                            trigger={'hover'}
                            placement="rightBottom"
                            overlayClassName="popover-p-0 he-dashboard-active-order-updates"
                            destroyTooltipOnHide
                            visible={isVisible}
                            onVisibleChange={setIsVisible}
                        >
                            <div className="w-full relative">
                                <NavItem
                                    onClick={onHEDashboard}
                                    className={`fill-darwin-neutral_new-800 ${getAclViewClassName(
                                        SREV2.HXNG_ORDERS,
                                    )}`}
                                    label="Orders"
                                    active={
                                        currentRoute &&
                                        currentRoute.navBarName === NAV_BAR_HE_DASHBOARD
                                            ? true
                                            : false
                                    }
                                    icon={
                                        <svg
                                            className="w-20 h-20 fill-darwin-neutral_new-800 mb-4"
                                            viewBox="0 0 16 16"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M9.85352 1.80273L10.666 2.61523L13.4844 5.45898L14.2969 6.27148C14.5508 6.52539 14.5508 6.90625 14.2969 7.13477C14.0684 7.38867 13.6875 7.38867 13.459 7.13477L13.0781 6.75391L5.96875 13.8633C5.46094 14.3711 4.80078 14.625 4.11523 14.625C2.66797 14.625 1.5 13.457 1.5 12.0098C1.5 11.3242 1.75391 10.6641 2.26172 10.1562L9.37109 3.04688L8.99023 2.66602C8.73633 2.4375 8.73633 2.05664 8.99023 1.80273C9.21875 1.57422 9.59961 1.57422 9.85352 1.80273ZM6.01953 8.125H5.99414H9.98047L12.2148 5.89062L10.2344 3.91016L6.01953 8.125Z" />
                                        </svg>
                                    }
                                    showTooltip={false}
                                />
                                {Number(activeOrderUpdatesCount) > 0 && (
                                    <p className="popup-overlay-show absolute top-0 right-8 h-16 min-w-16 flex items-center justify-center px-4 text-white bg-red-500 font-700 text-10 rounded-full">
                                        {activeOrderUpdatesCount}
                                    </p>
                                )}
                            </div>
                        </Popover>
                    )}
                    {
                        <NavItem
                            onClick={onPaymentsDashboard}
                            className={`fill-darwin-neutral_new-800 ${getAclViewClassName(
                                SREV2.PAY_DASH,
                            )}`}
                            label="Payments"
                            active={
                                currentRoute && currentRoute.navBarName === NAV_BAR_PAYMENTS
                                    ? true
                                    : false
                            }
                            icon={
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="fill-darwin-neutral_new-800"
                                >
                                    <rect width="24" height="24" rx="8" fill="none" />
                                    <path d="M7 5.75C7 5.34375 7.3125 5 7.75 5H16.25C16.6562 5 17 5.34375 17 5.75C17 6.1875 16.6562 6.5 16.25 6.5H13.5938C14.1562 7.125 14.5312 7.90625 14.6875 8.75H16.25C16.6562 8.75 17 9.09375 17 9.5C17 9.9375 16.6562 10.25 16.25 10.25H14.6875C14.3125 12.4062 12.4688 14 10.25 14H10.0625L15.1562 17.6562C15.5 17.9062 15.5938 18.375 15.3438 18.6875C15.0938 19.0312 14.625 19.125 14.3125 18.875L7.3125 13.875C7.03125 13.6875 6.90625 13.3438 7.03125 13.0312C7.125 12.7188 7.40625 12.5 7.75 12.5H10.25C11.625 12.5 12.8125 11.5625 13.125 10.25H7.75C7.3125 10.25 7 9.9375 7 9.5C7 9.09375 7.3125 8.75 7.75 8.75H13.125C12.8125 7.46875 11.625 6.5 10.25 6.5H7.75C7.3125 6.5 7 6.1875 7 5.75Z" />
                                </svg>
                            }
                        />
                    }
                    <NavItem
                        onClick={onAnalytics}
                        className="fill-darwin-neutral_new-800"
                        label="Analytics"
                        active={
                            currentRoute && currentRoute.navBarName === NAV_BAR_ANALYTICS
                                ? true
                                : false
                        }
                        icon={
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                className="fill-darwin-neutral_new-800"
                            >
                                <rect width="24" height="24" rx="8" fill="none" />
                                <path d="M5.5 16.75C5.5 17.1875 5.8125 17.5 6.25 17.5H19.25C19.6562 17.5 20 17.8438 20 18.25C20 18.6875 19.6562 19 19.25 19H6.25C5 19 4 18 4 16.75V5.75C4 5.34375 4.3125 5 4.75 5C5.15625 5 5.5 5.34375 5.5 5.75V16.75ZM14.5 13.5312C14.2188 13.8438 13.75 13.8438 13.4688 13.5312L10.9688 11.0625L8.5 13.5312C8.21875 13.8438 7.75 13.8438 7.46875 13.5312C7.15625 13.25 7.15625 12.7812 7.46875 12.4688L10.4688 9.46875C10.5938 9.34375 10.7812 9.25 10.9688 9.25C11.1875 9.25 11.375 9.34375 11.5 9.46875L14 11.9688L17.7188 8.21875C18 7.9375 18.4688 7.9375 18.75 8.21875C19.0625 8.53125 19.0625 9 18.75 9.28125L14.5 13.5312Z" />
                            </svg>
                        }
                    />

                    <NavItem
                        onClick={onABHADashboard}
                        className={`fill-darwin-neutral_new-800 ${getAclViewClassName(SREV2.ABHA)}`}
                        label="ABHA"
                        active={
                            currentRoute && currentRoute.navBarName === NAV_BAR_ABHA_DASHBOARD
                                ? true
                                : false
                        }
                        icon={
                            currentRoute?.navBarName === NAV_BAR_ABHA_DASHBOARD ? (
                                <ABHAIcon isActive={true} />
                            ) : isABHA ? (
                                <ABHAIcon isActive={false} leafColor="#19A66A" dotColor="#D74B41" />
                            ) : (
                                <ABHAIcon isActive={false} />
                            )
                        }
                        showNewLabel
                    />

                    <NavItem
                        className="frill-widget fill-darwin-neutral_new-800"
                        onClick={() => track('NAV_BAR_OLVY')}
                        label="What's new"
                        showTooltip={false}
                        active={false}
                        icon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 576 512"
                                className="w-18 h-18 my-4 fill-darwin-neutral_new-800"
                            >
                                <path d="M576 64V448C576 465.7 561.7 480 544 480C526.3 480 512 465.7 512 448V64C512 46.33 526.3 32 544 32C561.7 32 576 46.33 576 64zM63.96 190.3L480 64V448L348.9 408.2C338.2 449.5 300.7 480 256 480C202.1 480 160 437 160 384C160 372.1 161.9 362.3 165.3 352.5L63.96 321.7C63.07 338.6 49.1 352 32 352C14.33 352 0 337.7 0 320V192C0 174.3 14.33 160 32 160C49.1 160 63.07 173.4 63.96 190.3V190.3zM302.9 394.2L211.3 366.4C209.2 371.9 208 377.8 208 384C208 410.5 229.5 432 256 432C278.1 432 298.2 415.8 302.9 394.2V394.2z" />
                            </svg>
                        }
                    />

                    {isIpdEnabled && (
                        <NavItem
                            onClick={onIpdList}
                            className="fill-darwin-neutral_new-800"
                            label="IPD"
                            active={
                                currentRoute && currentRoute.navBarName === NAV_BAR_IPD_LIST
                                    ? true
                                    : false
                            }
                            icon={ipdHospitalIcon('w-24 h-24 fill-darwin-neutral_new-800')}
                        />
                    )}
                </div>

                <div className="flex flex-col gap-8 items-center self-end w-full">
                    <NavItem
                        label="My Account"
                        icon={
                            <svg
                                className="w-16 h-14 fill-darwin-neutral_new-800 relative bottom-4"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M78.6 5C69.1-2.4 55.6-1.5 47 7L7 47c-8.5 8.5-9.4 22-2.1 31.6l80 104c4.5 5.9 11.6 9.4 19 9.4l54.1 0 109 109c-14.7 29-10 65.4 14.3 89.6l112 112c12.5 12.5 32.8 12.5 45.3 0l64-64c12.5-12.5 12.5-32.8 0-45.3l-112-112c-24.2-24.2-60.6-29-89.6-14.3l-109-109 0-54.1c0-7.5-3.5-14.5-9.4-19L78.6 5zM19.9 396.1C7.2 408.8 0 426.1 0 444.1C0 481.6 30.4 512 67.9 512c18 0 35.3-7.2 48-19.9L233.7 374.3c-7.8-20.9-9-43.6-3.6-65.1l-61.7-61.7L19.9 396.1zM512 144c0-10.5-1.1-20.7-3.2-30.5c-2.4-11.2-16.1-14.1-24.2-6l-63.9 63.9c-3 3-7.1 4.7-11.3 4.7L352 176c-8.8 0-16-7.2-16-16l0-57.4c0-4.2 1.7-8.3 4.7-11.3l63.9-63.9c8.1-8.1 5.2-21.8-6-24.2C388.7 1.1 378.5 0 368 0C288.5 0 224 64.5 224 144l0 .8 85.3 85.3c36-9.1 75.8 .5 104 28.7L429 274.5c49-23 83-72.8 83-130.5zM56 432a24 24 0 1 1 48 0 24 24 0 1 1 -48 0z" />
                            </svg>
                        }
                        onClick={() => {
                            window.open(`${HUB}/account`, '_blank');
                            track('GO_TO_MY_ACCOUNT[HUB]');
                        }}
                        showLabel={true}
                        showTooltip={false}
                        className={getAclViewClassName(SREV2.MY_ACCOUNT)}
                    />
                    {/* {business?.id ? (
                        business?.logo ? (
                            <img src={business?.logo} alt="logo" className="w-32 h-32" />
                        ) : (
                            <AntTooltip
                            placement="right"
                            content={business?.name || 'Business account'}
                            >
                            {businessLogo('w-24 h-24')}
                            </AntTooltip>
                            )
                        ) : null}{' '} */}
                    <div className="border-b border-neutral-7 w-50 mb-72"></div>
                </div>
                <AnimatePresence>
                    {currentOrder &&
                        (currentOrder.type === ACTIVE_TAB.LABS ? (
                            <BookLabTestsDialog
                                closeMe={() => viewOrder(undefined)}
                                formData={[]}
                                pxid={pxId}
                                patientid={patientId}
                                name={patientName}
                                phone={phoneNumber}
                                gender={gender}
                                age={age}
                                orderId={orderId}
                                key={orderId}
                            />
                        ) : currentOrder.type === ACTIVE_TAB.MEDS ? (
                            <OrderMedicineDialog
                                closeMe={() => viewOrder(undefined)}
                                formData={[]}
                                pxid={pxId}
                                patientId={patientId}
                                name={patientName}
                                phone={phoneNumber}
                                gender={gender}
                                age={age}
                                orderId={orderId}
                                key={orderId}
                                clinicPin=""
                            />
                        ) : null)}
                </AnimatePresence>
            </div>
        </>
    );
};
